import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import apiClient from '../services/api';
import { AuthState, AuthResponse, User } from '../types/auth';
import {
  clearStoredAccessToken,
  clearStoredUser,
  getStoredAccessToken,
  getStoredUser,
  setStoredAccessToken,
  setStoredUser,
} from '../services/authStorage';

interface AuthContextType extends AuthState {
  login: (identifier: string, password: string) => Promise<void>;
  logout: () => void;
  refreshAccessToken: () => Promise<void>;
  updateUser: (user: User) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    accessToken: null,
    isAuthenticated: false,
    isLoading: true,
  });

  useEffect(() => {
    const loadAuthState = async () => {
      try {
        const accessToken = getStoredAccessToken();
        const userStr = getStoredUser();

        if (accessToken && userStr) {
          const user = JSON.parse(userStr);
          if (user && typeof user.role === 'object' && user.role !== null && 'name' in user.role) {
            setAuthState({
              user,
              accessToken,
              isAuthenticated: true,
              isLoading: false,
            });
            return;
          } else {
            clearStoredAccessToken();
            clearStoredUser();
          }
        }

        try {
          const refreshResponse = await apiClient.post<{ success: boolean; data: { accessToken: string } }>('/auth/refresh', {});
          const refreshedAccessToken = refreshResponse.data.data.accessToken;
          setStoredAccessToken(refreshedAccessToken);

          const meResponse = await apiClient.get<{ success: boolean; data: User }>('/auth/me', {
            headers: {
              Authorization: `Bearer ${refreshedAccessToken}`,
            },
          });

          const freshUser = meResponse.data.data;
          setStoredUser(JSON.stringify(freshUser));
          setAuthState({
            user: freshUser,
            accessToken: refreshedAccessToken,
            isAuthenticated: true,
            isLoading: false,
          });
        } catch {
          clearStoredAccessToken();
          if (!userStr) {
            clearStoredUser();
          }
          setAuthState((prev) => ({ ...prev, isLoading: false }));
        }
      } catch (error) {
        console.error('Error loading auth state:', error);
        setAuthState((prev) => ({ ...prev, isLoading: false }));
      }
    };

    loadAuthState();
  }, []);

  // Listen for auth:logout events from api.ts interceptor (SPA-safe redirect)
  useEffect(() => {
    const handleForceLogout = () => {
      setAuthState({
        user: null,
        accessToken: null,
        isAuthenticated: false,
        isLoading: false,
      });
    };

    window.addEventListener('auth:logout', handleForceLogout);
    return () => window.removeEventListener('auth:logout', handleForceLogout);
  }, []);

  useEffect(() => {
    if (!authState.isAuthenticated) return;

    const REFRESH_INTERVAL = 14 * 60 * 1000; // Refresh every 14 minutes (before 15min expiry)

    const interval = setInterval(() => {
      refreshAccessToken().catch((error) => {
        console.error('Auto-refresh failed:', error);
        logout();
      });
    }, REFRESH_INTERVAL);

    return () => clearInterval(interval);
  }, [authState.isAuthenticated]);

  const refreshAccessToken = useCallback(async () => {
    try {
      const response = await apiClient.post<{ success: boolean; data: { accessToken: string } }>('/auth/refresh', {});
      const { accessToken } = response.data.data;

      setStoredAccessToken(accessToken);

      setAuthState((prev) => ({
        ...prev,
        accessToken,
      }));
    } catch (error) {
      console.error('Token refresh failed:', error);
      logout();
      throw error;
    }
  }, []);

  const login = useCallback(async (identifier: string, password: string) => {
    try {
      const response = await apiClient.post<{ success: boolean; data: AuthResponse }>('/auth/login', {
        identifier,
        password,
      });

      const { user, accessToken } = response.data.data;

      setStoredAccessToken(accessToken);
      setStoredUser(JSON.stringify(user));

      setAuthState({
        user,
        accessToken,
        isAuthenticated: true,
        isLoading: false,
      });
    } catch (error: any) {
      const errorMessage = error.response?.data?.error?.message || 'Login failed';
      throw new Error(errorMessage);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      const accessToken = getStoredAccessToken();
      if (accessToken) {
        await apiClient.post('/auth/logout');
      }
    } catch (error) {
      console.error('Logout API call failed:', error);
    } finally {
      clearStoredAccessToken();
      clearStoredUser();

      setAuthState({
        user: null,
        accessToken: null,
        isAuthenticated: false,
        isLoading: false,
      });
    }
  }, []);

  const updateUser = useCallback((user: User) => {
    setStoredUser(JSON.stringify(user));
    setAuthState((prev) => ({
      ...prev,
      user,
    }));
  }, []);

  const value: AuthContextType = {
    ...authState,
    login,
    logout,
    refreshAccessToken,
    updateUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
